import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import {
  Zap,
  Brain,
  Swords,
  Gamepad2
} from 'lucide-react';

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  "headline": "Why We Built Trivia Rush: A Stock Market Higher or Lower Game",
  "description": "Learn why we created Trivia Rush, a fast-paced higher or lower game that builds your intuition for company scale using real market cap, revenue, and EPS data.",
  "url": "https://wallstreetle.com/blog/trivia-rush",
  "datePublished": "2026-04-17",
  "dateModified": "2026-04-17",
  "author": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "publisher": { "@type": "Organization", "name": "Wallstreetle", "url": "https://wallstreetle.com" },
  "image": "https://wallstreetle.com/assets/share-link-image.png"
};

export default function TriviaRushBlogPage() {
  return (
    <>
      <Helmet>
        <title>Wallstreetle | Why We Built Trivia Rush</title>
        <meta
          name="description"
          content="Discover why we built Trivia Rush, a fast-paced higher or lower stock market game. Compare companies on market cap, revenue, and EPS to build real investing intuition."
        />
        <meta
          name="keywords"
          content="stock market trivia, higher or lower stocks, stock market game, market cap quiz, stock trivia game, compare company size, revenue comparison game, stock knowledge quiz"
        />
        <link rel="canonical" href="https://wallstreetle.com/blog/trivia-rush" />

        <meta property="og:title" content="Wallstreetle | Why We Built Trivia Rush" />
        <meta property="og:description" content="A fast-paced higher or lower game that builds your intuition for company scale using real financial data." />
        <meta property="og:url" content="https://wallstreetle.com/blog/trivia-rush" />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:title" content="Wallstreetle | Why We Built Trivia Rush" />
        <meta property="twitter:description" content="A fast-paced higher or lower stock market game using real financial data." />
        <meta property="twitter:image" content="https://wallstreetle.com/assets/share-link-image.png" />

        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Zap className="w-4 h-4" /> Behind the Game
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif font-bold text-foreground mb-4">
            Why We Built Trivia Rush
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Most people can't tell you whether Apple's revenue is bigger than Amazon's. We built a game that fixes that.
          </p>
        </div>

        {/* Section: The Idea */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">The idea behind Trivia Rush</h2>
          <div className="prose dark:prose-invert max-w-none text-muted-foreground space-y-4">
            <p>
              Ask someone which company is worth more, Tesla or Walmart, and most people hesitate. Ask them whether Netflix or Disney has higher revenue, and the guesses are all over the place.
            </p>
            <p>
              These aren't trick questions. They're the kind of basic scale intuition that separates casual observers from informed investors. If you don't have a rough sense of how big a company is, it's very hard to evaluate whether its stock price makes sense.
            </p>
            <p>
              I wanted to build something that drills this intuition in the most addictive way possible: a simple higher-or-lower game, but with real stock market data.
            </p>
          </div>
        </section>

        {/* Section: How It Works */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">How Trivia Rush works</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            {[
              { step: '1', title: 'Pick your metric', desc: 'Choose to play with Market Cap, Revenue, or EPS. Each metric tests a different dimension of company knowledge.' },
              { step: '2', title: 'Higher or lower?', desc: 'You see two companies side by side. One shows its value. You guess whether the other is higher or lower.' },
              { step: '3', title: 'Build your streak', desc: 'Every correct answer extends your streak. One wrong answer and it\'s over. How far can you go?' },
              { step: '4', title: 'Climb the leaderboard', desc: 'Your best streak is recorded. Compete against the community to see who has the sharpest market instincts.' },
            ].map((item) => (
              <Card key={item.step} className="p-5">
                <div className="flex items-start gap-4">
                  <div className="flex items-center justify-center w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400 font-bold text-sm shrink-0">
                    {item.step}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* Section: Battle Rush */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-6">Battle Rush: making it social</h2>
          <div className="grid gap-5 md:grid-cols-3">
            {[
              { title: 'Challenge your friends', desc: 'Create a private room and share the code. Up to 8 players can join a single battle.', icon: Swords, color: 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' },
              { title: '3 lives, no safety net', desc: 'Everyone answers the same questions in real time. Get three wrong and you\'re eliminated. Last one standing wins.', icon: Zap, color: 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400' },
              { title: 'Real-time competition', desc: 'Watch your opponents\' lives tick down as you play. The pressure of a live audience makes every guess feel higher-stakes.', icon: Brain, color: 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' },
            ].map((item) => {
              const Icon = item.icon;
              return (
                <Card key={item.title} className="p-6 hover:shadow-lg transition-shadow">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${item.color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Section: Why It Matters */}
        <section className="mb-14">
          <h2 className="text-2xl font-serif font-bold text-foreground mb-4">Building intuition, one guess at a time</h2>
          <Card className="p-6 sm:p-8 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30 border-red-200 dark:border-red-800">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-lg shrink-0">
                <Gamepad2 className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  After a few rounds of Trivia Rush, something clicks. You start to develop a feel for company scale. You know that Microsoft's market cap is in the trillions, that Spotify's revenue is surprisingly smaller than you'd think, and that some oil companies have enormous earnings per share.
                </p>
                <p>
                  That <strong className="text-foreground">calibrated sense of scale</strong> is one of the most valuable things an investor can have, and Trivia Rush builds it in sixty-second bursts.
                </p>
              </div>
            </div>
          </Card>
        </section>

        {/* CTA */}
        <Card className="bg-gradient-to-r from-primary to-primary/80 text-white p-8 sm:p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-3">
            Think you know the market?
          </h2>
          <p className="text-white/90 mb-6 max-w-xl mx-auto">
            Test your stock market instincts with Trivia Rush. Higher or lower — it's harder than you think.
          </p>
          <Link
            to="/trivia-rush"
            className="inline-block bg-white text-primary font-semibold px-8 py-3 rounded-lg hover:bg-white/90 transition-colors"
          >
            Play Trivia Rush
          </Link>
        </Card>
      </div>
    </>
  );
}
